"use client";

import axios from "axios";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, Loader2 } from "lucide-react";

import { Button } from "@/components/shadcn/button";
import { Skeleton } from "@/components/shadcn/skeleton";
import { SpotToast } from "@/components/SpotToast";
import { DATA_KEYS } from "@/utils/useRemoteData";

import type { ProviderInfo } from "./types";

interface DeviceAuthSession {
  session_id: string;
  verification_url: string;
  user_code: string;
  expires_in_seconds: number;
  poll_interval_seconds: number;
}

interface DeviceAuthPollResult {
  status: "pending" | "connected" | "failed" | "expired";
  credential_id?: string | null;
  error?: string | null;
}

/**
 * Device-code sign-in panel: starts a session, shows the one-time code and
 * verification link, and polls until the provider reports it approved. The
 * dialog stays mounted throughout — no redirect.
 */
export function DeviceAuthConnect({
  integrationType,
  displayName,
  provider,
  onSuccess,
  credentialId,
}: {
  integrationType: string;
  displayName: string;
  provider: ProviderInfo;
  onSuccess: () => void;
  // Re-auth mode: the completed sign-in updates this credential row in
  // place instead of creating a new connection.
  credentialId?: string;
}) {
  const queryClient = useQueryClient();
  const [attempt, setAttempt] = useState(0);
  const [copied, setCopied] = useState(false);
  const [terminal, setTerminal] = useState<DeviceAuthPollResult | null>(null);

  const deviceAuthUrl = provider.device_auth_url ?? "";

  const {
    data: session,
    isLoading: starting,
    error: startError,
  } = useQuery({
    queryKey: ["device-auth-start", integrationType, deviceAuthUrl, attempt],
    queryFn: async () => {
      const response = await axios.post<DeviceAuthSession>(
        `/api/backend${deviceAuthUrl}`,
        {}
      );
      return response.data;
    },
    enabled: !!deviceAuthUrl,
    staleTime: Infinity,
    retry: false,
  });

  const polling = !!session && terminal === null;
  const { data: poll } = useQuery({
    queryKey: [
      "device-auth-poll",
      integrationType,
      session?.session_id,
      attempt,
    ],
    queryFn: async () => {
      const response = await axios.post<DeviceAuthPollResult>(
        `/api/backend${deviceAuthUrl}/${session?.session_id}`,
        {},
        { params: credentialId ? { credential_id: credentialId } : {} }
      );
      return response.data;
    },
    enabled: polling,
    refetchInterval: polling
      ? (session?.poll_interval_seconds ?? 3) * 1000
      : false,
    retry: false,
  });

  useEffect(() => {
    if (!poll || poll.status === "pending" || terminal !== null) return;
    setTerminal(poll);
    if (poll.status === "connected") {
      SpotToast.success(`${displayName} connected successfully.`);
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.INTEGRATIONS] });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.INTEGRATIONS_CONFIG],
      });
      onSuccess();
    }
  }, [poll, terminal, displayName, queryClient, onSuccess]);

  const handleCopy = async () => {
    if (!session) return;
    await navigator.clipboard.writeText(session.user_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleRetry = () => {
    setTerminal(null);
    setAttempt((n) => n + 1);
  };

  if (starting) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  if (startError || !session) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-destructive">
          Could not start the sign-in. Make sure the plugin is running, then try
          again.
        </p>
        <Button variant="outline" size="sm" onClick={handleRetry}>
          Try again
        </Button>
      </div>
    );
  }

  if (terminal && terminal.status !== "connected") {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-destructive">
          {terminal.status === "expired"
            ? "The code expired before it was used."
            : (terminal.error ?? "The sign-in failed.")}
        </p>
        <Button variant="outline" size="sm" onClick={handleRetry}>
          Get a new code
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Open the link below and enter this one-time code to connect your{" "}
        {displayName} account.
      </p>
      <button
        type="button"
        onClick={handleCopy}
        className="flex items-center justify-center gap-3 rounded-lg border border-border bg-card px-4 py-4 cursor-pointer hover:border-ring transition-colors"
        title="Copy code"
      >
        <span className="font-mono text-2xl tracking-widest text-foreground">
          {session.user_code}
        </span>
        {copied ? (
          <Check size={18} className="text-success" />
        ) : (
          <Copy size={18} className="text-muted-foreground" />
        )}
      </button>
      <a
        href={session.verification_url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
      >
        {session.verification_url}
        <ExternalLink size={14} />
      </a>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 size={14} className="animate-spin" />
        Waiting for approval: the code expires in{" "}
        {Math.round(session.expires_in_seconds / 60)} minutes.
      </div>
    </div>
  );
}
